<template>
  <article
    :class="[cardLayoutClass, cardFlippedClass]"
    class="card card--feature"
  >
    <div class="card__inner">
      <MediaCardImage
        :bg-color="mediaBgColor"
        :card-url="pageLink"
        :card-url-title="title"
        :fit="mediaFit"
        :max-width="720"
        :media="media"
        class="media--card"
      />
      <div class="card__content">
        <div>
          <TagsList
            :audiences="audiences"
            :size="!isVerticalCard ? 'lg' : null"
            :sorting="isDefaultTagsSorting"
            :topics="topics"
            :use-audience-as-primary="isPrimaryAudience"
          />
          <BlockLink v-if="title" :label="title" :link="pageLink">
            <h3>
              {{ title }}
            </h3>
          </BlockLink>
          <p v-if="introduction" class="card__intro">
            <span>
              {{ introduction }}
            </span>
          </p>
        </div>
        <BlockLink
          v-if="!isVerticalCard"
          :aria-label="title"
          :link="pageLink"
          class="button button--secondary button--xl hidden-xs"
        >
          <span>
            {{ cta }}
          </span>
          <i class="icon icon--lg">
            <Icon name="fa6-solid:arrow-right" />
          </i>
        </BlockLink>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import type { CustomCardStoryblok } from '~/types/storyblok'
const props = withDefaults(
  defineProps<{
    blok: CustomCardStoryblok
    customStory?: StoryContentBlok
    cardType?: string
    isFlipped?: boolean
  }>(),
  {
    cardType: 'horizontal',
    isFlipped: false,
    customStory: () => undefined,
  },
)
const storyContent = computed(() => props.blok?.content)
const {
  customLink,
  cta,
  title,
  introduction,
  isPrimaryAudience,
  audiences,
  isDefaultTagsSorting,
  topics,
  mediaBgColor,
  mediaFit,
  media,
} = useStoryContent({ ...toRefs(props), storyContent })
const pageLink = computed(() => customLink.value || '/' + props.blok.full_slug)
const isFlippedCard = computed(
  () => props.cardType === 'flipped' || props.isFlipped,
)
const isVerticalCard = computed(() => props.cardType === 'vertical')
const cardLayoutClass = computed(() =>
  isVerticalCard.value ? 'card--vertical' : 'card--horizontal',
)
const cardFlippedClass = computed(() =>
  isFlippedCard.value ? 'card--flipped' : '',
)
</script>

<style lang="less" scoped>
.card.card--feature {
  //Default styles vertical, mobile-first
  .card__inner {
    .flex-col();

    .card__content {
      padding: @gutter-sm @gutter @gutter;

      h3 {
        .font-xl();
        .font-headings();

        @media @tablet {
          .font-black();
        }

        @media @wide {
          .font-2xl();
        }
      }

      .button {
        align-self: flex-start;
      }

      .tag-list {
        margin-bottom: @gutter-sm;
      }

      p.card__intro {
        .font-sm();
        .font-body();

        margin: @gutter-xs 0 @gutter-lg;
      }
    }
  }

  // Layouts

  &.card--vertical {
    .card__inner {
      .card__content {
        .button {
          padding: @gutter-xxs @gutter;

          span {
            .font-size(16, 32);
            .font-bold();
          }
        }
      }
    }
  }

  &.card--horizontal {
    .media {
      flex-basis: auto;
    }

    .card__inner {
      .card__content {
        h3 {
          .font-2xl();
          .font-black();
          .space(0);
        }
      }
    }

    @media @mobile-xl, @tablet {
      .card__inner {
        .flex-row();

        .media {
          flex-basis: calc(50% - (@72px / 2));
        }

        .card__content {
          height: auto;
          padding: @gutter-lg @gutter-xl @gutter-xl;

          .tag-list {
            margin-bottom: @gutter;
          }

          p.card__intro {
            .h4();
            .text-normal();

            margin: @gutter 0 @gutter-lg;
          }
        }
      }

      //Flipped horizontal
      &.card--flipped {
        .card__inner {
          .media {
            order: 2;
          }

          .card__content {
            order: 1;
          }
        }
      }
    }

    @media @desktop {
      .card__inner {
        .card__content {
          padding: @gutter-xxl @gutter-xxl @60px @72px;

          h3 {
            .font-3xl();
          }
        }
      }
    }
  }
}
</style>

<style lang="less">
.card.card--feature {
  &.card--horizontal {
    .media {
      .media__container {
        @media @desktop {
          .image-aspect-7-5();
        }
      }
    }
  }

  &.card--vertical {
    .media {
      .media__container {
        @media @desktop {
          .image-aspect-16-9();
        }
      }
    }
  }
}
</style>
